// Shared status → semantic colour map (item 69). Every document type has its own
// status enum (ProposalStatus, InvoiceStatus, …) but the COLOUR semantics are
// shared by MEANING, so this maps the human-readable status LABEL to one of a few
// categories and a Chakra colour scheme — defined ONCE, reused by the grid status
// badge and the status-change menu. Uses fixed semantic colours (not the
// per-company brand accent) so "Paid" always reads green, even for a red-branded
// company.

export type StatusCategory = 'success' | 'declined' | 'void' | 'progress' | 'pending';

// Order matters: most specific / positive wins (e.g. "PaidEntered" → success
// before the "entered"→pending fallback; a voided doc beats everything).
export const statusCategory = (label?: string, isVoid?: boolean): StatusCategory => {
  if (isVoid) return 'void';
  const s = (label ?? '').toLowerCase();
  if (s.includes('void')) return 'void';
  // item 1376 (Chad: "in his portal it says reversed and in mine it says voided. what is
  // correct") — REVERSED is the word (item 1167: an admin reversed a mis-accrual; it stays
  // visible, out of totals, reopenable). It is dead-row styling like void, but it keeps its
  // own honest label; without this it fell through to 'pending' and lit up amber.
  if (s.includes('revers')) return 'void';
  if (s.includes('declined') || s.includes('donotenter')) return 'declined';
  // item 63 — recurring schedule states: Cancelled reads like declined, Paused holds
  if (s.includes('cancel')) return 'declined';
  if (s.includes('paused')) return 'pending';
  if (
    s.includes('paid')
    || s.includes('complete')
    || s.includes('approved')
    || s.includes('accepted')
    || s.includes('deposited')
    || s.includes('active') // recurring "Active"
  ) return 'success';
  // item 116: WO "Open" = in-flight (blue); invoice "Entered" = being processed (blue)
  if (s.includes('progress') || s.includes('generated') || s.includes('open') || s.includes('entered') || s.includes('ordered')) return 'progress';
  // item 116: WO "Closed" = done (green, same as complete/approved)
  if (s.includes('closed') || s.includes('finalized')) return 'success';
  // pending / new / draft / pending finalization / anything else
  return 'pending';
};

// Chakra colour scheme per category (drives Tag tint + text).
export const statusScheme: Record<StatusCategory, string> = {
  success: 'green',
  declined: 'red',
  void: 'gray',
  progress: 'blue',
  pending: 'orange',
};

// Soft glow used on the Aurora skin only (translucent so it reads as a halo).
export const statusGlow: Record<StatusCategory, string> = {
  success: 'rgba(72, 187, 120, 0.45)',
  declined: 'rgba(245, 101, 101, 0.45)',
  void: 'rgba(160, 174, 192, 0.30)',
  progress: 'rgba(66, 153, 225, 0.45)',
  pending: 'rgba(237, 137, 54, 0.45)',
};

export const schemeFor = (label?: string, isVoid?: boolean): string => statusScheme[statusCategory(label, isVoid)];
export const glowFor = (label?: string, isVoid?: boolean): string => statusGlow[statusCategory(label, isVoid)];

// Humanize a PascalCase enum label so it reads — and WRAPS — as words (item 100):
// "InvoicesGenerated" → "Invoices Generated", "DoNotEnter" → "Do Not Enter". Only
// splits a lowercase→uppercase boundary, so already-spaced / ALL-CAPS labels (e.g.
// the proposal "APPROVED DEPOSITED") are left untouched.
export const humanizeStatus = (label?: string): string => (label ?? '').replace(/([a-z])([A-Z])/g, '$1 $2');
